import { lazy, Suspense } from "react";
import { Route, Switch, Redirect } from "wouter";
import { RequireSuperAdmin, RequireTenant, RequireAuth } from "./routes/guards";
import { SignIn } from "./pages/auth/SignIn";
import { PublicRequestPage } from "./pages/public/RequestPage";
import { PublicChatPage } from "./pages/public/ChatPage";
import { ReferPage } from "./pages/public/ReferPage";
import { SignAgreementPage } from "./pages/public/SignAgreementPage";
import { EstimatePage } from "./pages/public/EstimatePage";
import { PortalPage } from "./pages/public/PortalPage";
import { FeedbackPage, TechPage } from "./pages/public/CustomerPages";
import { Welcome } from "./pages/auth/Welcome";
import { AdminShell } from "./pages/admin/AdminShell";
import { CustomerShell } from "./pages/customer/CustomerShell";
import { useAuth } from "./hooks/useAuth";

// Dev only (dropped from production builds): sample-data previews of shared UI.
const UiGallery = import.meta.env.DEV ? lazy(() => import("./pages/dev/UiGallery")) : null;

function Home() {
  const { status } = useAuth();
  if (status === "loading") return null;
  if (status === "super_admin") return <Redirect to="/admin" />;
  if (status === "customer") return <Redirect to="/app" />;
  if (status === "unprovisioned") return <Redirect to="/welcome" />;
  return <Redirect to="/signin" />;
}

export function App() {
  return (
    <Switch>
      <Route path="/signin" component={SignIn} />
      {UiGallery ? (
        <Route path="/dev/ui">
          <Suspense fallback={null}>
            <UiGallery />
          </Suspense>
        </Route>
      ) : null}
      {/* Public intake — no auth, shared by tenants as app.clevis.pro/request/{slug} */}
      <Route path="/request/:slug">{(params) => <PublicRequestPage slug={params.slug ?? ""} />}</Route>
      {/* Public website chat that books visits into Requests (pending approval) */}
      <Route path="/chat/:slug">{(params) => <PublicChatPage slug={params.slug ?? ""} />}</Route>
      {/* Clevis Relay: a customer's referral link lands here */}
      <Route path="/refer/:slug/:code">{(params) => <ReferPage slug={params.slug ?? ""} code={params.code ?? ""} />}</Route>
      {/* Public e-sign — token-gated via ?t=, emailed by emailAgreementSignLink */}
      <Route path="/sign/:tenantId/:agreementId">
        {(params) => <SignAgreementPage tenantId={params.tenantId ?? ""} agreementId={params.agreementId ?? ""} />}
      </Route>
      {/* Public estimate — token-gated via ?t=: read, sign, pay the deposit, or decline */}
      <Route path="/estimate/:tenantId/:estimateId">
        {(params) => <EstimatePage tenantId={params.tenantId ?? ""} estimateId={params.estimateId ?? ""} />}
      </Route>
      {/* Customer portal: sign in by emailed link (#k=…) or texted code */}
      <Route path="/portal" component={PortalPage} />
      {/* Public pages from a customer text: who's coming, and how did we do (token-gated) */}
      <Route path="/t/:token">{(params) => <TechPage token={params.token ?? ""} />}</Route>
      <Route path="/r/:token">{(params) => <FeedbackPage token={params.token ?? ""} />}</Route>
      <Route path="/welcome">
        <RequireAuth>
          <Welcome />
        </RequireAuth>
      </Route>
      <Route path="/admin" nest>
        <RequireSuperAdmin>
          <AdminShell />
        </RequireSuperAdmin>
      </Route>
      <Route path="/app" nest>
        <RequireTenant>
          <CustomerShell />
        </RequireTenant>
      </Route>
      <Route path="/" component={Home} />
      <Route>
        <Home />
      </Route>
    </Switch>
  );
}
